import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Smartphone, BatteryCharging } from "lucide-react";
import { api } from "@/lib/api";

const meta = {
  display: {
    Icon: Smartphone,
    img: "https://images.pexels.com/photos/288479/pexels-photo-288479.jpeg",
    bullets: ["Original-Qualität", "1 Werktag Reparatur", "6 Monate Garantie (OLED)"],
  },
  akku: {
    Icon: BatteryCharging,
    img: "https://images.unsplash.com/photo-1639776738932-956082f0b704?crop=entropy&cs=srgb&fm=jpg&q=85",
    bullets: ["100% Kapazität", "Schnelles Laden", "Zertifizierte Zellen"],
  },
};

function ServiceCard({ s, index, onSelect }) {
  const m = meta[s.id] || meta.display;
  const Icon = m.Icon;
  return (
    <motion.button
      type="button"
      onClick={() => onSelect(s.title)}
      data-testid={`service-card-${s.id}`}
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: index * 0.12 }}
      className="group relative text-left overflow-hidden rounded-3xl border border-white/10 bg-[#0a0a0a] p-8 lg:p-10 flex flex-col justify-between min-h-[440px] hover:border-white/25 transition-colors duration-500"
    >
      <img
        src={m.img}
        alt=""
        className="absolute inset-0 w-full h-full object-cover opacity-15 group-hover:opacity-25 group-hover:scale-105 transition-[opacity,transform] duration-700"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/60 to-transparent" />

      <div className="relative z-10 flex items-start justify-between">
        <div className="w-14 h-14 rounded-full border border-white/15 flex items-center justify-center text-white group-hover:bg-white group-hover:text-[#050505] transition-colors duration-500">
          <Icon size={24} />
        </div>
        <span className="font-mono text-xs text-[#777] uppercase tracking-widest">
          0{index + 1}
        </span>
      </div>

      <div className="relative z-10">
        <div className="flex flex-wrap gap-2 mb-5">
          {m.bullets.map((b) => (
            <span
              key={b}
              className="text-xs font-mono text-[#aaa] border border-white/10 rounded-full px-3 py-1"
            >
              {b}
            </span>
          ))}
        </div>
        <h3 className="font-display text-white text-4xl lg:text-5xl leading-tight">
          {s.title}
        </h3>
        <p className="text-[#999] text-sm mt-3 max-w-sm leading-relaxed">{s.desc}</p>
        <div className="mt-6 flex items-center gap-4">
          <span className="font-mono text-white text-xl">{s.price}</span>
          <span className="text-[#666] text-sm group-hover:text-white transition-colors duration-300">
            Jetzt beauftragen →
          </span>
        </div>
      </div>
    </motion.button>
  );
}

export default function Services() {
  const [services, setServices] = useState([]);
  const navigate = useNavigate();
  const onSelect = (title) => navigate("/beauftragen", { state: { preselect: title } });

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
  }, []);

  return (
    <section id="services" data-testid="services" className="relative z-10 bg-[#050505] py-24 lg:py-36">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#888] mb-4">
              Unsere Spezialität
            </p>
            <h2 className="font-display text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.95] max-w-2xl">
              Zwei Reparaturen.<br />Perfekt beherrscht.
            </h2>
          </div>
          <p className="text-[#999] max-w-sm leading-relaxed">
            Wir konzentrieren uns bewusst auf Display und Akku – genau deshalb
            sind wir darin so schnell und zuverlässig. Klick, um direkt zu beauftragen.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {services.map((s, i) => (
            <ServiceCard key={s.id} s={s} index={i} onSelect={onSelect} />
          ))}
        </div>
      </div>
    </section>
  );
}
